import React, { useMemo } from 'react';
import { Card, Stack, Typography } from '@locus-taxy/encore-mui';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { EncoreFormRenderer } from '@locus-taxy/encore-form';
import type { FormRendererRef } from '@locus-taxy/encore-form';
import { getTestId } from '@locus-taxy/encore-utils';
import { TestIdAction } from '../../utils/testIdUtils';
import type { ProfileViewMode, ViewField } from './profileTypes';
import { ProfileViewRenderer } from './ProfileViewRenderer';
import { buildPersonalInfoSchema, buildPersonalInfoInitialData } from './profileFormSchemas';

interface PersonalInfoSectionProps {
  readonly mode: ProfileViewMode;
  readonly entityData: Record<string, unknown> | undefined;
  readonly clientId: string;
  readonly formRef: React.RefObject<FormRendererRef | null>;
  readonly onChange?: (data: Record<string, unknown>) => void;
  readonly viewFields: ViewField[];
}

export function PersonalInfoSection({
  mode, entityData, clientId, formRef, onChange, viewFields,
}: PersonalInfoSectionProps) {
  const { t } = useTranslation();

  const schema = useMemo(() => buildPersonalInfoSchema(t), [t]);
  const formInitialData = useMemo(() => buildPersonalInfoInitialData(entityData), [entityData]);
  const additionalContext = useMemo(() => ({ entityData }), [entityData]);

  return (
    <Card
      elevation={0}
      variant="outlined"
      data-testid={getTestId({ action: TestIdAction.VIEW, object: 'personal-info-section', page: 'account' })}
    >
      <Stack spacing={3} padding={4}>
        <Typography variant="h6" fontWeight={600} color="text.primary">
          {t('userDetails.personalInformation', 'Personal Information')}
        </Typography>

        {mode === 'view' ? (
          <ProfileViewRenderer fields={viewFields} columns={2} />
        ) : (
          <EncoreFormRenderer
            ref={formRef}
            schema={schema as any}
            clientId={clientId}
            initialData={formInitialData}
            additionalContext={additionalContext}
            onChange={onChange}
            routeContext={{ entity: 'user', page: 'profile' }}
          />
        )}
      </Stack>
    </Card>
  );
}
